// client/src/dnd/views/InteractiveMap.jsx
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { MapContainer, ImageOverlay, Pane, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import { Grid3x3, MapPin, UserPlus2, CloudFog } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import FitImageBounds from './Interactive_parts/FitImageBounds.js';
import isMobileScreen from './Interactive_parts/isMobileScreen.js';
import ClickHandler from './Interactive_parts/ClickHandler.js';
import ShapeEditToolbar from './Interactive_parts/ShapeEditToolbar';
import VideoOverlay from '../components/VideoOverlay';
import GridOverlay from './Interactive_parts/GridOverlay.js';
import TokenLayer from './Interactive_parts/TokenLayer.js';
import GeomanControl from './Interactive_parts/GeomanControl.js';
import PixiLayer from '../components/PixiLayer.js';
import TeleportManager from './Interactive_parts/TeleportManager.js';
import { HARDCODED_MARKERS } from '../config/hardcodedMarkers';
import AssetOverlay from '../components/pixi_parts/assetOverlay';
import DemoParts from './Interactive_parts/DemoParts.js';
import SpellMenu from '../components/pixi_parts/SpellMenu';
import DraggableImageOverlay from './Interactive_parts/DraggableImageOverlay.js';
import { ShapePathingRunner } from '../components/pathing_tools/PathingManager.js';
import ViewerPathingLoader from './Interactive_parts/ViewerPathingLoader.js';
import DemoWallsGeoman from './Interactive_parts/DemoWallsGeoman.js';
import PersonalPinModal from './Interactive_parts/PersonalPinModal.js';
import useAssetEditing from './Interactive_parts/useAssetEditing.js';

const RENDER_LEAFLET_ASSETS = true;
const TOKENS_API_URL = '/api/tabletop/tokens';

function toStandardCoords({ x, y }, imageSize) {
  return { x, y };
}
function fromStandardCoords({ x, y }, imageSize) {
  return { x, y };
}

function getPersonalPinKey(mapId, userId) {
  return `personal_pins_${mapId}_${userId}`;
}

function getGridToggleKey(mapId) {
  return `pointatlas_show_grid_${mapId}`;
}

const apiBase = '/api/polygons';

function isVideoFile(url) {
  return typeof url === "string" && (url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.ogg'));
}

function isWallBehavior(behaviorType, raw) {
  const bType =
    behaviorType ??
    raw?.behavior_type ??
    raw?.behaviorType ??
    '';
  const v = (bType ?? '').toString().trim().toLowerCase();
  return v === '' || v === 'wall';
}

function extractWallPolygonsFromShapes(shapes) {
  const result = [];
  for (const shapeRaw of shapes) {
    let geometry;
    try {
      geometry = typeof shapeRaw.geometry === "string"
        ? JSON.parse(shapeRaw.geometry)
        : shapeRaw.geometry;
    } catch {
      continue;
    }

    const behaviorType = shapeRaw.behaviorType ?? shapeRaw.behavior_type ?? '';
    if (!isWallBehavior(behaviorType, shapeRaw)) {
      continue;
    }

    if (
      geometry &&
      geometry.type === 'Polygon' &&
      geometry.coordinates &&
      geometry.coordinates[0]
    ) {
      result.push(
        geometry.coordinates[0].map(([lng, lat]) =>
          toStandardCoords({ x: lng, y: lat }, [0, 0])
        )
      );
    }
  }
  return result;
}

function resolveAssetUrl(u) {
  if (!u) return '';
  const s = String(u).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith('data:')) return s;
  if (s.startsWith('/')) return s;
  return `/${s.replace(/^\/+/, '')}`;
}

function normalizeId(value) {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
}

function getAssignedUserIdFromToken(token) {
  return normalizeId(token?.controlled_user_id ?? token?.user_id ?? null);
}

function normalizeTokenForState(t, imageSize) {
  return {
    ...toStandardCoords(t, imageSize),
    emits_light: typeof t.emits_light === 'string' ? (t.emits_light === '1' || t.emits_light === 'true' ? 1 : 0) : (t.emits_light ? 1 : 0),
    reach: typeof t.reach === 'string' ? parseFloat(t.reach) : (typeof t.reach === 'number' ? t.reach : 0),
    intensity: typeof t.intensity === 'string' ? parseFloat(t.intensity) : (typeof t.intensity === 'number' ? t.intensity : 1),
    flicker: typeof t.flicker === 'string' ? (t.flicker === '1' || t.flicker === 'true' ? 1 : 0) : (t.flicker ? 1 : 0),
    colour: t.colour || '#fff',
    type: t.type || 'player',
    light_type: t.light_type || 'none',
    user_id: t.user_id || null,
    controlled_user_id: t.controlled_user_id || null,
    id: t.id || null,
    label: t.label || '',
    icon_url: t.icon_url || '',
    visible_to_all: typeof t.visible_to_all === 'string' ? (t.visible_to_all === '1' || t.visible_to_all === 'true' ? 1 : 0) : (t.visible_to_all ? 1 : 0),
  };
}

function sortTokensStable(arr) {
  return [...arr].sort((a, b) => {
    const aId = String(a?.id ?? '');
    const bId = String(b?.id ?? '');
    if (aId < bId) return -1;
    if (aId > bId) return 1;
    return 0;
  });
}

function tokensEqual(a, b) {
  return JSON.stringify(sortTokensStable(a || [])) === JSON.stringify(sortTokensStable(b || []));
}

function getCharacterTokenTitle(tokenType, isOwner) {
  if (tokenType === 'npc') {
    return isOwner ? 'Edit NPC Token' : 'NPC Token';
  }
  return isOwner ? 'Edit Player Token' : 'Player Token';
}

function PlayerTokenModal({ token, isOwner, onSave, onDelete, onClose }) {
  const [modalData, setModalData] = useState(token || null);

  useEffect(() => {
    setModalData(token || null);
  }, [token]);

  if (!modalData) return null;

  const infoRowStyle = {
    marginBottom: 10,
    padding: '8px 10px',
    borderRadius: 8,
    background: '#19191e',
    border: '1px solid #35353f',
    fontSize: 14,
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'transparent',
          zIndex: 22599,
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed',
          left: '50%',
          top: '38%',
          transform: 'translate(-50%, -50%)',
          background: '#222225',
          padding: 30,
          borderRadius: 14,
          color: '#fff',
          zIndex: 22600,
          boxShadow: '0 4px 34px #000d',
          minWidth: 350,
          maxWidth: 420,
        }}
      >
        <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 17 }}>
          {getCharacterTokenTitle(modalData.type, isOwner)}
        </div>

        {isOwner ? (
          <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
            <label>
              <span style={{ fontSize: 15 }}>Label</span>
              <input
                type="text"
                value={modalData.label || ''}
                onChange={e => setModalData(md => ({ ...md, label: e.target.value }))}
                style={{ width: '100%', padding: 8, borderRadius: 7, border: '1px solid #888', background: '#19191e', color: '#fff', fontSize: 15 }}
              />
            </label>
            <label>
              <span style={{ fontSize: 15 }}>Colour</span>
              <input
                type="color"
                value={modalData.colour || (modalData.type === 'npc' ? '#22c55e' : '#3b82f6')}
                onChange={e => setModalData(md => ({ ...md, colour: e.target.value }))}
                style={{ marginLeft: 13, width: 38, height: 28, verticalAlign: 'middle' }}
              />
              <span style={{ marginLeft: 8, fontSize: 14, color: modalData.type === 'npc' ? '#86efac' : '#9ec5ff' }}>
                {String(modalData.colour || (modalData.type === 'npc' ? '#22c55e' : '#3b82f6')).toUpperCase()}
              </span>
            </label>
            <label>
              <span style={{ fontSize: 15 }}>Visible To All</span>
              <input
                type="checkbox"
                checked={!!modalData.visible_to_all}
                onChange={e => setModalData(md => ({ ...md, visible_to_all: e.target.checked ? 1 : 0 }))}
                style={{ marginLeft: 10 }}
              />
            </label>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={infoRowStyle}>
              <strong>Name:</strong> {modalData.label || (modalData.type === 'npc' ? 'NPC' : 'Player')}
            </div>
            <div style={infoRowStyle}>
              <strong>Type:</strong> {modalData.type || 'player'}
            </div>
            {!!modalData.controlled_username && (
              <div style={infoRowStyle}>
                <strong>Controlled By:</strong> {modalData.controlled_username}
              </div>
            )}
            {!!modalData.icon_url && (
              <div style={{ ...infoRowStyle, display: 'flex', alignItems: 'center', gap: 10 }}>
                <strong>Icon:</strong>
                <img src={modalData.icon_url} alt="token icon" style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover' }} />
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 13, marginTop: 14 }}>
          {isOwner && (
            <>
              <button
                style={{
                  background: '#4cc153', color: '#fff', border: 'none', borderRadius: 7,
                  padding: '5px 18px', fontWeight: 700, fontSize: 15, cursor: 'pointer'
                }}
                onClick={() => onSave(modalData)}
              >Save</button>
              <button
                style={{
                  background: '#f44c2b', color: '#fff', border: 'none', borderRadius: 7,
                  padding: '5px 14px', fontWeight: 700, fontSize: 15, cursor: 'pointer'
                }}
                onClick={() => onDelete(modalData)}
              >Delete</button>
            </>
          )}
          <button
            style={{
              background: 'none', border: '1px solid #aaa', borderRadius: 7,
              color: '#fff', padding: '5px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer'
            }}
            onClick={onClose}
          >Close</button>
        </div>
      </div>
    </>
  );
}

function InteractiveMap({
  mapData,
  imageSize,
  user,
  isMapOwnerOrFounder,
  visibleZoneIds,
  onZonesChanged,
  extraRegionMaps = [],
  parentMapId = null,
  parentMapName = 'Parent Map'
}) {
  const containerRef = useRef(null);
  const resizeHideTimerRef = useRef(null);
  const resizeFadeStartTimerRef = useRef(null);
  const resizeFadeRemoveTimerRef = useRef(null);
  const lastFetchedTokensJsonRef = useRef('');
  const tokenEndpointMissingRef = useRef(false);

  const [personalPins, setPersonalPins] = useState([]);
  const [pinsLoaded, setPinsLoaded] = useState(false);
  const [placingPin, setPlacingPin] = useState(false);
  const [editPinIdx, setEditPinIdx] = useState(null);
  const [modalPin, setModalPin] = useState(null);
  const [isResizingMap, setIsResizingMap] = useState(false);
  const [resizeBlackoutVisible, setResizeBlackoutVisible] = useState(false);
  const [resizeBlackoutOpacity, setResizeBlackoutOpacity] = useState(0);
  const [playerTokenModalData, setPlayerTokenModalData] = useState(null);

  const [showGrid, setShowGrid] = useState(() => {
    if (!mapData?.id) return true;
    const key = getGridToggleKey(mapData.id);
    const val = localStorage.getItem(key);
    if (val === null || val === undefined) return true;
    return val === "true";
  });
  useEffect(() => {
    if (!mapData?.id) return;
    const key = getGridToggleKey(mapData.id);
    localStorage.setItem(key, showGrid ? "true" : "false");
  }, [showGrid, mapData?.id]);

  const [selectedShape, setSelectedShape] = useState(null);
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [savingShape, setSavingShape] = useState(false);
  const [addingToken, setAddingToken] = useState(false);
  const [fogOfWar, setFogOfWar] = useState(false);

  const [tokens, setTokens] = useState([]);

  const shapeLayerMap = React.useRef({});
  const [wallPolygons, setWallPolygons] = useState([]);

  const [isMobile, setIsMobile] = useState(isMobileScreen());
  const [toolboxBottom, setToolboxBottom] = useState(98);

  const [gridConfig, setGridConfig] = useState(null);
  useEffect(() => {}, [gridConfig]);
  const metreInPixels = gridConfig?.metre_in_pixels ?? 1;

  const [hardcodedMarker, setHardcodedMarker] = useState(
    mapData?.id ? HARDCODED_MARKERS[mapData.id] || null : null
  );

  const [pathingEntries, setPathingEntries] = useState([]);
  const [isGeomanActive, setIsGeomanActive] = useState(false);

  const [spellMenuState, setSpellMenuState] = useState({
    open: false,
    x: 0,
    y: 0,
    origin: null,
    extra: null,
  });

  const [demoWallPolygons, setDemoWallPolygons] = useState([]);

  useEffect(() => {
    tokenEndpointMissingRef.current = false;
    lastFetchedTokensJsonRef.current = '';
    setHardcodedMarker(mapData?.id ? HARDCODED_MARKERS[mapData.id] || null : null);
  }, [mapData?.id]);

  useEffect(() => {
    const handler = () => setIsMobile(isMobileScreen());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setToolboxBottom(98);
      return;
    }
    function updateToolboxBottom() {
      const bar = document.getElementById('mobile-tab-bar');
      if (bar) {
        const rect = bar.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const offset = Math.max(windowHeight - rect.top, 0);
        setToolboxBottom(offset + 5);
      } else {
        setToolboxBottom(25);
      }
    }
    updateToolboxBottom();
    window.addEventListener('resize', updateToolboxBottom);
    return () => window.removeEventListener('resize', updateToolboxBottom);
  }, [isMobile]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    let first = true;

    const handleResize = () => {
      if (first) {
        first = false;
        return;
      }
      setIsResizingMap(true);
      if (resizeHideTimerRef.current) {
        clearTimeout(resizeHideTimerRef.current);
      }
      resizeHideTimerRef.current = setTimeout(() => {
        setIsResizingMap(false);
      }, 140);
    };

    const observer = new ResizeObserver(handleResize);
    observer.observe(el);

    return () => {
      observer.disconnect();
      if (resizeHideTimerRef.current) {
        clearTimeout(resizeHideTimerRef.current);
        resizeHideTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isResizingMap) {
      if (resizeFadeStartTimerRef.current) {
        clearTimeout(resizeFadeStartTimerRef.current);
        resizeFadeStartTimerRef.current = null;
      }
      if (resizeFadeRemoveTimerRef.current) {
        clearTimeout(resizeFadeRemoveTimerRef.current);
        resizeFadeRemoveTimerRef.current = null;
      }
      setResizeBlackoutVisible(true);
      setResizeBlackoutOpacity(1);
      return;
    }

    if (!resizeBlackoutVisible) return;

    resizeFadeStartTimerRef.current = setTimeout(() => {
      setResizeBlackoutOpacity(0);
    }, 20);

    resizeFadeRemoveTimerRef.current = setTimeout(() => {
      setResizeBlackoutVisible(false);
    }, 2000);

    return () => {
      if (resizeFadeStartTimerRef.current) {
        clearTimeout(resizeFadeStartTimerRef.current);
        resizeFadeStartTimerRef.current = null;
      }
      if (resizeFadeRemoveTimerRef.current) {
        clearTimeout(resizeFadeRemoveTimerRef.current);
        resizeFadeRemoveTimerRef.current = null;
      }
    };
  }, [isResizingMap, resizeBlackoutVisible]);

  useEffect(() => {
    return () => {
      if (resizeFadeStartTimerRef.current) {
        clearTimeout(resizeFadeStartTimerRef.current);
        resizeFadeStartTimerRef.current = null;
      }
      if (resizeFadeRemoveTimerRef.current) {
        clearTimeout(resizeFadeRemoveTimerRef.current);
        resizeFadeRemoveTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapData?.id || !user?.id) return;
    const key = getPersonalPinKey(mapData.id, user.id);
    const pins = JSON.parse(localStorage.getItem(key) || '[]');
    setPersonalPins(Array.isArray(pins) ? pins.map(pin => toStandardCoords(pin, imageSize)) : []);
    setPinsLoaded(true);
  }, [mapData?.id, user?.id, imageSize]);

  useEffect(() => {
    if (!mapData?.id || !user?.id || !pinsLoaded) return;
    const key = getPersonalPinKey(mapData.id, user.id);
    localStorage.setItem(key, JSON.stringify(personalPins.map(pin => fromStandardCoords(pin, imageSize))));
  }, [personalPins, mapData?.id, user?.id, pinsLoaded, imageSize]);

  useEffect(() => {
    if (!mapData?.id) {
      setGridConfig(null);
      return;
    }
    fetch(`/api/tabletop/grids/${mapData.id}`)
      .then(res => res.json())
      .then(data => {
        const grid = Array.isArray(data) && data.length > 0 ? data[0] : null;
        setGridConfig(grid);
      })
      .catch(() => setGridConfig(null));
  }, [mapData?.id]);

  const fetchTokens = useCallback(async () => {
    if (!mapData?.id || tokenEndpointMissingRef.current) return;

    try {
      const res = await fetch(`${TOKENS_API_URL}?mapId=${mapData.id}`, {
        credentials: 'include',
      });

      if (res.status === 404) {
        tokenEndpointMissingRef.current = true;
        return;
      }

      if (!res.ok) return;

      const data = await res.json();
      const nextTokens = (Array.isArray(data) ? data : []).map(t => normalizeTokenForState(t, imageSize));
      const nextJson = JSON.stringify(sortTokensStable(nextTokens));

      if (nextJson !== lastFetchedTokensJsonRef.current) {
        lastFetchedTokensJsonRef.current = nextJson;
        setTokens(prev => (tokensEqual(prev, nextTokens) ? prev : nextTokens));
      }
    } catch {}
  }, [mapData?.id, imageSize]);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  useEffect(() => {
    if (!mapData?.id || tokenEndpointMissingRef.current) return;

    const intervalId = window.setInterval(() => {
      fetchTokens();
    }, 1000);

    const handleFocus = () => {
      fetchTokens();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchTokens();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [mapData?.id, fetchTokens]);

  function refreshWallPolygons() {
    if (!shapeLayerMap.current) return;
    const wallPolys = [];
    Object.values(shapeLayerMap.current).forEach(layer => {
      if (!layer || !layer.toGeoJSON) return;

      const behaviorType =
        (layer.options && (layer.options.behaviorType ?? layer.options.behavior_type)) ??
        (layer._shapeMeta && (layer._shapeMeta.behaviorType ?? layer._shapeMeta.behavior_type)) ??
        '';

      if (!isWallBehavior(behaviorType, layer._shapeMeta || null)) {
        return;
      }

      const geo = layer.toGeoJSON();
      if (geo && geo.geometry && geo.geometry.type === 'Polygon') {
        wallPolys.push(
          geo.geometry.coordinates[0].map(([lng, lat]) =>
            toStandardCoords({ x: lng, y: lat }, imageSize)
          )
        );
      }
    });
    setWallPolygons(wallPolys.slice());
  }

  useEffect(() => {
    if (isMapOwnerOrFounder) {
      refreshWallPolygons();
    } else {
      if (!mapData?.id) return;
      fetch(`${apiBase}?mapId=${mapData.id}`)
        .then(res => res.json())
        .then(shapes => {
          setWallPolygons(extractWallPolygonsFromShapes(shapes));
        })
        .catch(() => {
          setWallPolygons([]);
        });
    }
  }, [
    tokens, showGrid, fogOfWar, selectedShape, selectedLayer, mapData, imageSize,
    addingToken, placingPin, savingShape, pinsLoaded, visibleZoneIds, isMapOwnerOrFounder
  ]);

  function handlePinMarkerClick(idx) {
    const pin = personalPins[idx];
    setModalPin(pin);
    setEditPinIdx(idx);
  }

  function handleModalSavePin({ label, colour }) {
    if (editPinIdx === null) {
      setPersonalPins(pins => [...pins, { ...modalPin, label, colour }]);
    } else {
      setPersonalPins(pins => pins.map((pin, i) => i === editPinIdx ? { ...pin, label, colour } : pin));
    }
    setModalPin(null);
    setEditPinIdx(null);
  }

  function handleModalDeletePin() {
    if (editPinIdx !== null) setPersonalPins(pins => pins.filter((_, i) => i !== editPinIdx));
    setModalPin(null);
    setEditPinIdx(null);
  }

  function handleModalCancelPin() {
    setModalPin(null);
    setEditPinIdx(null);
  }

  function handleShapeToolbarChange(newShape) {
    const merged = {
      ...(selectedShape || {}),
      ...newShape,
    };

    setSelectedShape(merged);

    if (selectedLayer) {
      selectedLayer.setStyle({
        color: merged.strokeColor,
        fillColor: merged.fillColor,
        opacity: merged.strokeOpacity,
        fillOpacity: merged.fillOpacity,
      });
      selectedLayer.options.color = merged.strokeColor;
      selectedLayer.options.fillColor = merged.fillColor;
      selectedLayer.options.opacity = merged.strokeOpacity;
      selectedLayer.options.fillOpacity = merged.fillOpacity;
      if (merged.label !== undefined) selectedLayer.options.label = merged.label;
      if (merged.behaviorType !== undefined) {
        selectedLayer.options.behaviorType = merged.behaviorType;
        selectedLayer.options.behavior_type = merged.behaviorType;
        if (selectedLayer._shapeMeta) {
          selectedLayer._shapeMeta.behaviorType = merged.behaviorType;
          selectedLayer._shapeMeta.behavior_type = merged.behaviorType;
        }
      }
      if (selectedLayer._shapeMeta) {
        selectedLayer._shapeMeta = {
          ...selectedLayer._shapeMeta,
          ...merged,
        };
      }
    }

    setPathingEntries(prev =>
      Array.isArray(prev)
        ? prev.map(entry =>
            entry.id === merged.id
              ? {
                  ...entry,
                  shape: {
                    ...entry.shape,
                    ...merged,
                  },
                }
              : entry
          )
        : prev
    );
  }

  function openShapeToolbar(shape, layer) {
    if (!isMapOwnerOrFounder) return;
    const effectiveBehaviorType =
      shape.behaviorType ??
      shape.behavior_type ??
      layer?.options?.behaviorType ??
      layer?.options?.behavior_type ??
      '';

    if (layer) {
      if (!layer._shapeMeta) {
        layer._shapeMeta = {
          ...shape,
          behaviorType: effectiveBehaviorType,
          behavior_type: effectiveBehaviorType,
        };
      } else {
        layer._shapeMeta.behaviorType = effectiveBehaviorType;
        layer._shapeMeta.behavior_type = effectiveBehaviorType;
      }
      layer.options.behaviorType = effectiveBehaviorType;
      layer.options.behavior_type = effectiveBehaviorType;
    }

    setSelectedShape({
      ...shape,
      strokeColor: layer?.options.color ?? shape.strokeColor,
      fillColor: layer?.options.fillColor ?? shape.fillColor,
      strokeOpacity: layer?.options.opacity ?? shape.strokeOpacity,
      fillOpacity: layer?.options.fillOpacity ?? shape.fillOpacity,
      behaviorType: effectiveBehaviorType,
    });
    setSelectedLayer(layer || (shape && shapeLayerMap.current[shape.id]) || null);
  }

  async function handleSaveShape(shape) {
    if (!shape) return;
    setSavingShape(true);
    let geometry = shape.geometry;
    let radius = shape.radius;
    let shapeType = shape.shapeType;
    if (selectedLayer) {
      if (typeof selectedLayer.getRadius === "function") {
        const center = selectedLayer.getLatLng();
        geometry = {
          type: "Point",
          coordinates: [center.lng, center.lat]
        };
        radius = selectedLayer.getRadius();
        shapeType = "Circle";
      } else {
        geometry = selectedLayer.toGeoJSON().geometry;
        shapeType = geometry.type || "";
        radius = null;
      }
      if (selectedLayer.pm && selectedLayer.pm.enabled()) {
        selectedLayer.pm.disable();
      }
      selectedLayer.setStyle({
        color: shape.strokeColor,
        fillColor: shape.fillColor,
        opacity: shape.strokeOpacity,
        fillOpacity: shape.fillOpacity,
      });
      selectedLayer.options.color = shape.strokeColor;
      selectedLayer.options.fillColor = shape.fillColor;
      selectedLayer.options.opacity = shape.strokeOpacity;
      selectedLayer.options.fillOpacity = shape.fillOpacity;
      if (shape.label !== undefined) selectedLayer.options.label = shape.label;
      if (shape.behaviorType !== undefined) {
        selectedLayer.options.behaviorType = shape.behaviorType;
        selectedLayer.options.behavior_type = shape.behaviorType;
      }
      if (selectedLayer._shapeMeta) {
        selectedLayer._shapeMeta = {
          ...selectedLayer._shapeMeta,
          geometry,
          radius,
          shapeType,
          strokeColor: shape.strokeColor,
          fillColor: shape.fillColor,
          strokeOpacity: shape.strokeOpacity,
          fillOpacity: shape.fillOpacity,
          label: shape.label,
          behaviorType: shape.behaviorType,
          behavior_type: shape.behaviorType,
          sprite: shape.sprite ?? selectedLayer._shapeMeta.sprite,
          tokenIconUrl: shape.tokenIconUrl ?? selectedLayer._shapeMeta.tokenIconUrl,
          tokenColor: shape.tokenColor ?? selectedLayer._shapeMeta.tokenColor,
          tokenName: shape.tokenName ?? selectedLayer._shapeMeta.tokenName,
          tokenSpeed: typeof shape.tokenSpeed === 'number'
            ? shape.tokenSpeed
            : (selectedLayer._shapeMeta.tokenSpeed ?? 0.25),
          tokenDirection: shape.tokenDirection || selectedLayer._shapeMeta.tokenDirection || 'forward',
          tokenDefaultPosition: typeof shape.tokenDefaultPosition === 'number'
            ? shape.tokenDefaultPosition
            : (selectedLayer._shapeMeta.tokenDefaultPosition ?? 0.0),
          tokenAutoMove:
            shape.tokenAutoMove === 1 || shape.tokenAutoMove === true
              ? 1
              : (selectedLayer._shapeMeta.tokenAutoMove ?? 0),
        };
      }
    }

    const resolvedBehaviorType =
      shape.behaviorType ?? shape.behavior_type ?? null;
    const resolvedBehaviorProps =
      shape.behaviorProps ?? shape.behavior_props ?? undefined;
    const resolvedBehaviorState =
      shape.behaviorState ?? shape.behavior_state ?? undefined;

    const payload = {
      id: shape.id,
      geometry,
      radius,
      shapeType,
      fillColor: shape.fillColor,
      strokeColor: shape.strokeColor,
      strokeOpacity: shape.strokeOpacity,
      fillOpacity: shape.fillOpacity,
      label: shape.label,
      pattern: shape.pattern,
      behaviorType: resolvedBehaviorType,
      behavior_type: resolvedBehaviorType,
      behaviorProps: resolvedBehaviorProps,
      behavior_props: resolvedBehaviorProps,
      behaviorState: resolvedBehaviorState,
      behavior_state: resolvedBehaviorState,
      sprite: shape.sprite ?? undefined,
      tokenIconUrl: shape.tokenIconUrl ?? null,
      tokenColor:
        shape.tokenColor ||
        shape.strokeColor ||
        shape.fillColor ||
        null,
      tokenName: shape.tokenName ?? null,
      tokenSpeed:
        typeof shape.tokenSpeed === 'number' ? shape.tokenSpeed : 0.25,
      tokenDirection: shape.tokenDirection || 'forward',
      tokenDefaultPosition:
        typeof shape.tokenDefaultPosition === 'number'
          ? shape.tokenDefaultPosition
          : 0.0,
      tokenAutoMove:
        shape.tokenAutoMove === 1 || shape.tokenAutoMove === true ? 1 : 0,
    };

    try {
      await fetch(`${apiBase}/${shape.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setSelectedShape(prev =>
        prev
          ? { ...prev, ...payload }
          : { ...payload }
      );
      refreshWallPolygons();
      if (onZonesChanged) onZonesChanged();

      setPathingEntries(prev =>
        Array.isArray(prev)
          ? prev.map(entry =>
              entry.id === shape.id
                ? {
                    ...entry,
                    shape: {
                      ...entry.shape,
                      ...payload,
                    },
                  }
                : entry
            )
          : prev
      );
    } catch (err) {}
    setSavingShape(false);
    setSelectedLayer(null);
  }

  async function handleDeleteShape(shape, layer) {
    try {
      await fetch(`${apiBase}/${shape.id}`, { method: "DELETE" });
      if (layer && layer.remove) layer.remove();
      if (layer && layer._map && layer._map.removeLayer) {
        layer._map.removeLayer(layer);
      }
      if (shape && shape.id && shapeLayerMap.current) {
        delete shapeLayerMap.current[shape.id];
      }
      setSelectedShape(null);
      setSelectedLayer(null);
      refreshWallPolygons();
      onZonesChanged && onZonesChanged();
      setPathingEntries(prev =>
        Array.isArray(prev)
          ? prev.filter(entry => entry.id !== shape.id)
          : prev
      );
    } catch (err) {}
  }

  function handleAddToken(newToken) {
    setTokens(tokens => [...tokens, newToken]);
    setTimeout(() => {
      fetchTokens();
    }, 50);
  }

  function handleUpdateToken(updatedToken) {
    setTokens(tokens => tokens.map(t => t.id === updatedToken.id ? updatedToken : t));
  }

  async function handlePersistTokenPosition(updatedToken) {
    if (!updatedToken || !updatedToken.id || !mapData?.id) return;

    const payload = {
      ...updatedToken,
      map_id: mapData.id,
    };

    try {
      await fetch(`${TOKENS_API_URL}/${updatedToken.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      setTokens(prev =>
        prev.map(t => (t.id === updatedToken.id ? { ...t, ...updatedToken } : t))
      );

      setTimeout(() => {
        fetchTokens();
      }, 50);
      setTimeout(() => {
        fetchTokens();
      }, 400);
    } catch (err) {}
  }

  async function handleDeleteToken(tokenId) {
    if (!tokenId) return;
    try {
      await fetch(`${TOKENS_API_URL}/${tokenId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setTokens(prev => prev.filter(t => String(t.id) !== String(tokenId)));
      setPlayerTokenModalData(current => (String(current?.id) === String(tokenId) ? null : current));
      setTimeout(() => {
        fetchTokens();
      }, 50);
    } catch (err) {}
  }

  function handleMapClick(coord) {
    if (!placingPin) return;
    setModalPin({ x: coord.x, y: coord.y, label: "" });
    setPlacingPin(false);
    setEditPinIdx(null);
  }

  const {
    mapRef,
    overlayAssets,
    imageAssets,
    roofAssets,
    assetWalls,
    editingAsset,
    editingAssetBounds,
    editingAssetId,
    handleDragOver,
    handleDrop,
    handleEditingAssetChange,
  } = useAssetEditing({
    mapId: mapData?.id,
    imageSize,
  });

  const mapKey = mapData && imageSize ? `${mapData.id}_${imageSize[0]}x${imageSize[1]}` : 'no-map';

  const playerTokens = (tokens || [])
    .filter(t => t.type === 'player')
    .map(t => ({
      ...t,
      ...toStandardCoords({ x: t.x, y: t.y }, imageSize),
      id: t.id || undefined,
      label: t.label || "",
      type: 'player',
      colour: t.colour || "#3b82f6",
      user_id: t.user_id || null,
      controlled_user_id: t.controlled_user_id || t.user_id || null,
      icon_url: t.icon_url || "",
      emits_light: t.emits_light ? 1 : 0,
      flicker: t.flicker ? 1 : 0,
      reach: typeof t.reach === 'number' ? t.reach : (parseFloat(t.reach) || 0),
      intensity: typeof t.intensity === "number" ? t.intensity : (parseFloat(t.intensity) || 1),
      visible_to_all: t.visible_to_all ? 1 : 0,
    }));

  const npcTokens = (tokens || [])
    .filter(t => t.type === 'npc')
    .map(t => ({
      ...t,
      ...toStandardCoords({ x: t.x, y: t.y }, imageSize),
      id: t.id || undefined,
      label: t.label || "NPC",
      type: 'npc',
      colour: t.colour || "#22c55e",
      user_id: t.user_id || null,
      controlled_user_id: t.controlled_user_id || null,
      icon_url: t.icon_url || "",
      emits_light: t.emits_light ? 1 : 0,
      flicker: t.flicker ? 1 : 0,
      reach: typeof t.reach === 'number' ? t.reach : (parseFloat(t.reach) || 0),
      intensity: typeof t.intensity === "number" ? t.intensity : (parseFloat(t.intensity) || 1),
      visible_to_all: t.visible_to_all ? 1 : 0,
    }));

  const characterTokens = [...playerTokens, ...npcTokens];

  const lightMarkers = (tokens || [])
    .filter(t => t.emits_light && t.type === 'light')
    .map(t => ({
      ...toStandardCoords({ x: t.x, y: t.y }, imageSize),
      id: t.id || undefined,
      label: t.label || "",
      type: t.type || 'light',
      light_type: t.light_type || "none",
      reach: typeof t.reach === 'number' ? t.reach : (parseFloat(t.reach) || 0),
      reach_units: t.reach_units || "metres",
      intensity: typeof t.intensity === "number" ? t.intensity : 1,
      colour: t.colour || "#3b82f6",
      emits_light: t.emits_light ? 1 : 0,
      flicker: t.flicker ? 1 : 0,
    }));

  const viewerUserId = normalizeId(user?.id);

  const assignedPlayerTokensForViewer = React.useMemo(() => {
    if (!viewerUserId) return [];
    return playerTokens.filter(token => getAssignedUserIdFromToken(token) === viewerUserId);
  }, [playerTokens, viewerUserId]);

  const hasAssignedPlayerToken = assignedPlayerTokensForViewer.length > 0;

  const draggablePlayerTokens = React.useMemo(() => {
    if (isMapOwnerOrFounder) return characterTokens;
    if (hasAssignedPlayerToken) return assignedPlayerTokensForViewer;
    return [];
  }, [isMapOwnerOrFounder, characterTokens, hasAssignedPlayerToken, assignedPlayerTokensForViewer]);

  const viewerScopedPlayerTokens = React.useMemo(() => {
    if (isMapOwnerOrFounder) return playerTokens;
    if (hasAssignedPlayerToken) return assignedPlayerTokensForViewer;
    return playerTokens;
  }, [isMapOwnerOrFounder, playerTokens, hasAssignedPlayerToken, assignedPlayerTokensForViewer]);

  const viewerScopedPlayerTokenIdSet = React.useMemo(() => {
    return new Set(
      viewerScopedPlayerTokens
        .map(token => normalizeId(token?.id))
        .filter(Boolean)
    );
  }, [viewerScopedPlayerTokens]);

  const viewerScopedTokens = React.useMemo(() => {
    if (isMapOwnerOrFounder) return tokens;
    if (!hasAssignedPlayerToken) return tokens;
    return (tokens || []).filter(token => {
      if (token?.type !== 'player') return true;
      return viewerScopedPlayerTokenIdSet.has(normalizeId(token?.id));
    });
  }, [isMapOwnerOrFounder, hasAssignedPlayerToken, tokens, viewerScopedPlayerTokenIdSet]);

  const viewerHardcodedMarker = React.useMemo(() => {
    return hardcodedMarker || null;
  }, [hardcodedMarker]);

  const sightMarkers = viewerScopedPlayerTokens.map(t => ({
    id: t.id,
    x: t.x,
    y: t.y,
  }));

  const losWalls = React.useMemo(() => {
    const a = Array.isArray(wallPolygons) ? wallPolygons : [];
    const b = Array.isArray(assetWalls) ? assetWalls : [];
    const c = Array.isArray(demoWallPolygons) ? demoWallPolygons : [];
    return [...a, ...b, ...c];
  }, [wallPolygons, assetWalls, demoWallPolygons]);

  const safePathControls = {};

  function handleOpenSpellMenuAt(point) {
    const px = point && Number.isFinite(point.x) ? point.x : window.innerWidth / 2;
    const py = point && Number.isFinite(point.y) ? point.y : window.innerHeight / 2;
    const ox = point && point.origin && Number.isFinite(point.origin.x) ? point.origin.x : null;
    const oy = point && point.origin && Number.isFinite(point.origin.y) ? point.origin.y : null;

    setSpellMenuState({
      open: true,
      x: px,
      y: py,
      origin: ox !== null && oy !== null ? { x: ox, y: oy } : null,
      extra: point && point.extra && typeof point.extra === 'object'
        ? { ...point.extra }
        : {
            ignoreCollisionIds: Array.isArray(point?.ignoreCollisionIds) ? point.ignoreCollisionIds.slice() : [],
            sourceTokenId: point?.sourceTokenId != null ? String(point.sourceTokenId) : null,
            sourceTokenType: point?.sourceTokenType ? String(point.sourceTokenType).toLowerCase() : null,
          },
    });
  }

  function handleCloseSpellMenu() {
    setSpellMenuState(prev => ({
      ...prev,
      open: false,
    }));
  }

  function openPlayerTokenEditor(token) {
    if (!token || !isMapOwnerOrFounder) return;
    setPlayerTokenModalData({ ...token });
  }

  function openPlayerTokenInfo(token) {
    if (!token || isMapOwnerOrFounder) return;
    setPlayerTokenModalData({ ...token });
  }

  async function handleSavePlayerToken(modalData) {
    if (!modalData || !modalData.id || !mapData?.id) {
      setPlayerTokenModalData(null);
      return;
    }

    const payload = {
      ...modalData,
      map_id: mapData.id,
    };

    try {
      await fetch(`${TOKENS_API_URL}/${modalData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      setTokens(prev => prev.map(t => String(t.id) === String(modalData.id) ? { ...t, ...modalData } : t));
      setPlayerTokenModalData(null);
      setTimeout(() => {
        fetchTokens();
      }, 50);
    } catch (err) {}
  }

  async function handleDeletePlayerToken(modalData) {
    if (!modalData?.id) {
      setPlayerTokenModalData(null);
      return;
    }
    await handleDeleteToken(modalData.id);
    setPlayerTokenModalData(null);
  }

  const playerTokenModalOwner = !!isMapOwnerOrFounder;

  return (
    <div
      ref={containerRef}
      style={{ flex: "1 1 0%", minHeight: 0, minWidth: 0, position: "relative" }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <MapContainer
        key={mapKey}
        crs={L.CRS.Simple}
        bounds={[[0, 0], [imageSize[1], imageSize[0]]]}
        style={{ height: '100%', width: '100%', position: 'relative', zIndex: 10 }}
        minZoom={-4}
        maxZoom={4}
        zoom={0}
        zoomSnap={0.1}
        zoomDelta={0.5}
        scrollWheelZoom={true}
        zoomControl={false}
        zoomAnimation={false}
        markerZoomAnimation={false}
        fadeAnimation={false}
        inertia={false}
        whenCreated={(m) => { mapRef.current = m; }}
      >
        <Pane name="base-pane" style={{ zIndex: 200 }} />
        <Pane name="grid-pane" style={{ zIndex: 450 }} />
        <Pane name="assets-pane" style={{ zIndex: 400 }} />
        <Pane name="overlay-pane-high" style={{ zIndex: 1200 }} />
        <Pane name="topmost-pane" style={{ zIndex: 10000 }} />

        <ZoomControl position="bottomright" />

        {placingPin && (
          <ClickHandler
            onClick={handleMapClick}
            enabled={!isGeomanActive}
          />
        )}

        {isMapOwnerOrFounder && (
          <GeomanControl
            mapId={mapData.id}
            userId={user?.id}
            onZonesChanged={onZonesChanged}
            visibleZoneIds={visibleZoneIds}
            canEdit={isMapOwnerOrFounder}
            onShapeClick={openShapeToolbar}
            shapeLayerMap={shapeLayerMap}
            onPathingEntriesChange={setPathingEntries}
            onGeomanActiveChange={setIsGeomanActive}
          />
        )}

        {!isMapOwnerOrFounder && (
          <>
            <ViewerPathingLoader
              mapId={mapData.id}
              onPathingEntriesChange={setPathingEntries}
            />
            <DemoWallsGeoman
              imageSize={imageSize}
              onDemoWallsChange={setDemoWallPolygons}
            />
          </>
        )}

        {mapData.image_url && isVideoFile(mapData.image_url) ? (
          <VideoOverlay
            url={resolveAssetUrl(mapData.image_url)}
            bounds={[[0, 0], [imageSize[1], imageSize[0]]]}
          />
        ) : (
          <>
            <ImageOverlay
              url={resolveAssetUrl(mapData.image_url)}
              bounds={[[0, 0], [imageSize[1], imageSize[0]]]}
              pane="base-pane"
            />
            <FitImageBounds bounds={[[0, 0], [imageSize[1], imageSize[0]]]} />
          </>
        )}

        {RENDER_LEAFLET_ASSETS && (
          <AssetOverlay
            imageAssets={imageAssets}
            imageSize={imageSize}
            editingAssetId={editingAssetId}
          />
        )}

        <GridOverlay
          mapId={mapData.id}
          imageSize={imageSize}
          showGrid={showGrid}
          canEditGrid={isMapOwnerOrFounder}
          onGridConfigChange={setGridConfig}
        />

        <TokenLayer
          imageSize={imageSize}
          addTokenMode={addingToken}
          onFinishAddToken={() => setAddingToken(false)}
          tokens={tokens}
          setTokens={setTokens}
          mapId={mapData.id}
          user={user}
          isMapOwnerOrFounder={isMapOwnerOrFounder}
          gridConfig={gridConfig}
        />

        {pathingEntries.map(({ id, shape: s, layer }) => (
          <ShapePathingRunner
            key={id}
            shape={s}
            layer={layer}
            enabled={true}
            controls={safePathControls}
            showPoiLabels={true}
            metreInPixels={metreInPixels}
          />
        ))}

        {editingAsset && editingAssetBounds && (
          <DraggableImageOverlay
            url={resolveAssetUrl(editingAsset.url)}
            initialBounds={editingAssetBounds}
            pane="overlay-pane-high"
            zIndex={Number.isFinite(editingAsset.z_index) ? editingAsset.z_index : 9900}
            editing={true}
            onChange={handleEditingAssetChange}
          />
        )}

        {!editingAsset && (
          <PixiLayer
            mapId={mapData.id}
            imageSize={imageSize}
            hardcodedMarker={viewerHardcodedMarker || undefined}
            playerTokens={viewerScopedPlayerTokens}
            proximityPlayerTokens={playerTokens}
            draggablePlayerTokens={draggablePlayerTokens}
            renderedPlayerTokens={characterTokens}
            lightMarkers={lightMarkers}
            sightMarkers={sightMarkers}
            wallPolygons={losWalls}
            tokens={viewerScopedTokens}
            fogOfWar={fogOfWar}
            fogEnabled={fogOfWar}
            isMapOwnerOrFounder={isMapOwnerOrFounder}
            onAddToken={handleAddToken}
            onUpdateToken={handleUpdateToken}
            onPersistTokenPosition={handlePersistTokenPosition}
            onDeleteToken={handleDeleteToken}
            onPlayerTokenClick={(token) => {
              if (isMapOwnerOrFounder) {
                openPlayerTokenEditor(token);
              } else {
                openPlayerTokenInfo(token);
              }
            }}
            metreInPixels={metreInPixels}
            gridConfig={gridConfig}
            zIndex={9800}
            autoLoadAssets={false}
            roofAssets={roofAssets}
            overlayAssets={overlayAssets}
            spellsEnabled={true}
            onHardcodedMove={(pos) =>
              setHardcodedMarker(m => (m ? { ...m, reach: m.reach, x: pos.x, y: pos.y } : m))
            }
            onOpenSpellMenu={handleOpenSpellMenuAt}
          />
        )}

        <TeleportManager mapId={mapData.id} tokens={viewerScopedTokens} hardcodedMarker={viewerHardcodedMarker} />
      </MapContainer>

      {selectedShape && isMapOwnerOrFounder && (
        <ShapeEditToolbar
          shape={selectedShape}
          onChange={handleShapeToolbarChange}
          onSave={() => handleSaveShape(selectedShape)}
          onClose={() => { setSelectedShape(null); setSelectedLayer(null); }}
          onDelete={handleDeleteShape}
          saving={savingShape}
          layer={selectedLayer}
          canEdit={isMapOwnerOrFounder}
          extraRegionMaps={extraRegionMaps}
          parentMapId={parentMapId}
          parentMapName={parentMapName}
        />
      )}

      <div
        className="map-controls-cluster"
        style={
          isMobile
            ? {
                position: 'fixed',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1600,
                bottom: `${toolboxBottom}px`,
                background: '#222f',
                borderRadius: 16,
                padding: '4px 18px',
                boxShadow: '0 2px 14px #000b',
                display: 'flex',
                gap: 6
              }
            : undefined
        }
      >
        <button
          className={`map-controls-btn ${placingPin ? "active" : ""}`}
          title="Add Personal Pin"
          onClick={() => setPlacingPin(val => !val)}
        >
          <MapPin />
        </button>
        <button
          className="map-controls-btn"
          title={showGrid ? "Hide Grid" : "Show Grid"}
          onClick={() => setShowGrid(g => !g)}
        >
          <Grid3x3 />
        </button>
        <button
          className="map-controls-btn"
          title="Open Emitter Tool"
          onClick={() => window.open("/emitter-tool", "_blank")}
        >
          ✨
        </button>
        {isMapOwnerOrFounder && (
          <>
            <button
              className={`map-controls-btn ${addingToken ? "active" : ""}`}
              title="Add Player Token"
              onClick={() => setAddingToken(val => !val)}
            >
              <UserPlus2 />
            </button>

            <button
              className={`map-controls-btn ${fogOfWar ? "active" : ""}`}
              title="Toggle Fog of War"
              onClick={() => setFogOfWar(val => !val)}
            >
              <CloudFog />
            </button>
          </>
        )}
      </div>

      {hardcodedMarker && (
        <DemoParts
          hardcodedMarker={hardcodedMarker}
          setHardcodedMarker={setHardcodedMarker}
          isMobile={isMobile}
          toolboxBottom={toolboxBottom}
        />
      )}

      {modalPin && (
        <PersonalPinModal
          modalPin={modalPin}
          editPinIdx={editPinIdx}
          onChangeLabel={(value) => setModalPin({ ...modalPin, label: value })}
          onSave={() => handleModalSavePin(modalPin)}
          onDelete={handleModalDeletePin}
          onCancel={handleModalCancelPin}
        />
      )}

      {playerTokenModalData && (
        <PlayerTokenModal
          token={playerTokenModalData}
          isOwner={playerTokenModalOwner}
          onSave={handleSavePlayerToken}
          onDelete={handleDeletePlayerToken}
          onClose={() => setPlayerTokenModalData(null)}
        />
      )}

      <SpellMenu
        open={spellMenuState.open}
        x={spellMenuState.x}
        y={spellMenuState.y}
        spellOrigin={spellMenuState.origin}
        extra={spellMenuState.extra}
        onClose={handleCloseSpellMenu}
      />

      {resizeBlackoutVisible && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: '#000',
            opacity: resizeBlackoutOpacity,
            transition: isResizingMap ? 'none' : 'opacity 2000ms ease',
            zIndex: 200000,
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}

export default InteractiveMap;